import { useRouteMatches } from '@luminovo/design-system';
import * as Sentry from '@sentry/react';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import * as React from 'react';

import { ErrorFallback } from './ErrorBoundary';

export type ErrorBoundaryClearedOnNavigationProps = {
    children: React.ReactNode;
    /** Defaults to `ErrorFallback`. Keep it module-level: it's rendered as `<Fallback />`, so a new one remounts. */
    fallback?: Sentry.FallbackRender;
    /** Tags the report, as on `Sentry.ErrorBoundary`. */
    beforeCapture?: Sentry.ErrorBoundaryProps['beforeCapture'];
};

/** For layouts that outlive their pages: clears its error and resets query errors once the next page shows. */
export function ErrorBoundaryClearedOnNavigation({
    children,
    fallback = ErrorFallback,
    beforeCapture,
}: ErrorBoundaryClearedOnNavigationProps): JSX.Element {
    // Its own component, so a navigation re-renders the boundary, not the providers above it.
    // The leaf's: the page being left stays mounted until the next has loaded, and cleared sooner, would throw again.
    const pathname = useRouteMatches().at(-1)?.pathname ?? '';
    const current = React.useMemo(() => ({ pathname, fallback }), [pathname, fallback]);

    // The reset covers failed queries still watched; `removeFailedQueriesOnNavigation` removes the others.
    return (
        <QueryErrorResetBoundary>
            {({ reset }) => (
                <CurrentContext.Provider value={current}>
                    <Sentry.ErrorBoundary
                        beforeCapture={beforeCapture}
                        onReset={reset}
                        fallback={FallbackClearedOnNavigation}
                    >
                        {children}
                    </Sentry.ErrorBoundary>
                </CurrentContext.Provider>
            )}
        </QueryErrorResetBoundary>
    );
}

// Through context: Sentry renders `fallback` as a type, so one closing over the pathname would remount on navigation.
const CurrentContext = React.createContext<{ pathname: string; fallback: Sentry.FallbackRender }>({
    pathname: '',
    fallback: ErrorFallback,
});

/** Mounts when an error is caught, so it remembers the pathname it was caught at. */
const FallbackClearedOnNavigation: Sentry.FallbackRender = (props) => {
    const { pathname, fallback: Fallback } = React.useContext(CurrentContext);
    const [caughtAt] = React.useState(pathname);
    const { resetError } = props;
    React.useEffect(() => {
        if (pathname !== caughtAt) {
            resetError();
        }
    }, [pathname, caughtAt, resetError]);
    return <Fallback {...props} />;
};
